import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Icon } from "@/atoms/icon/Icon";
import AccountIcon from "@/atoms/icon/icons/account_nav.svg";
import ChevronDown from "@/atoms/icon/icons/chevron_down_small.svg";
import RepeatCash from "@/atoms/icon/icons/RepeatCash.svg";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTranslation } from "@/core/i18n";
import { goToSignIn, useIsAnonymousBrowsing } from "@/modules/anonymousBrowsing";
import { RewardProgressV2 } from "@/modules/rewards/RewardProgressV2/RewardProgressV2";
import { getNextRewardMilestone } from "@/modules/rewards/nextRewardMilestone";
import { getPlatform, getRewards, getUser } from "api/methods";
import { RewardStatus } from "api/types";
import { TextButton } from "atoms/text-button/TextButton";
import { formatCurrency } from "toolbox/Money";

export const DesktopRewards = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isAnonymous = useIsAnonymousBrowsing();
  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const { data: user } = useQuery({ ...getUser.keyed(), staleTime: 1000 * 60 * 2 });
  const { data: rewards, isFetching } = useQuery({
    ...getRewards.keyed({}),
    enabled: !!user && !user.isGuest && !isAnonymous,
  });

  if (!user || !platform) return null;

  if (isAnonymous || user.isGuest) {
    return (
      <TextButton
        className="max-w-[180px] gap-2 !pl-0 text-left !text-body1 desklg:max-w-none desklg:whitespace-nowrap"
        onClick={() => goToSignIn(navigate, location.pathname + location.search)}
      >
        {t("anonymousBrowsing.logInToEarnRewards")}
        <Icon svg={AccountIcon} size="medium" />
      </TextButton>
    );
  }

  const accountLinks = (
    <>
      <Link
        to="/rewards"
        className="flex min-h-11 items-center gap-1.5 whitespace-nowrap text-body2 text-brand-color"
        aria-label={`${t("walletLayout.repeatCash")} ${formatCurrency(user.repeatCashBalance, 0)}`}
      >
        <Icon svg={RepeatCash} size="xsmall" />
        {formatCurrency(user.repeatCashBalance, 0)}
      </Link>
      <Link to="/account" aria-label={t("topNav.account")}>
        <Icon svg={AccountIcon} />
      </Link>
    </>
  );

  if (!rewards) {
    return (
      <div className="flex items-center gap-3">
        <Link
          to="/rewards"
          aria-busy={isFetching}
          className="flex min-h-11 items-center rounded-lg px-2 text-body3 text-brand-color hover:bg-one"
        >
          {t("footer.rewards")}
        </Link>
        {accountLinks}
      </div>
    );
  }

  const available = rewards.filter(({ status }) => status === RewardStatus.Available);
  const next = getNextRewardMilestone(
    rewards.filter(({ status }) => status === RewardStatus.NotAvailable),
  );
  const summary = next
    ? t("rewardProgress.onlyVisitsUntilNextReward", {
        count: next.minimumVisits,
        visitWord: t("rewardProgress.nounVisit", { count: next.minimumVisits }),
      })
    : available.length > 0
      ? t("rewardProgressV2.rewardsWaiting", { count: available.length })
      : t("footer.rewards");

  return (
    <div className="flex items-center gap-3">
      <Popover key={location.pathname}>
        <PopoverTrigger className="flex min-h-11 max-w-[200px] items-center gap-2 rounded-lg px-2 text-left text-brand-color hover:bg-one focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
          <span className="flex min-w-0 flex-col gap-1.5">
            <span className="hidden text-body3 leading-tight desklg:block">{summary}</span>
            <span className="text-body3 desklg:hidden">{t("footer.rewards")}</span>
            {next && (
              <span
                aria-hidden
                className="h-1 w-full overflow-hidden rounded-full bg-brand-color/10"
              >
                <span
                  className="block h-full rounded-full bg-brand-color"
                  style={{ width: `${next.unlockProgressPercent}%` }}
                />
              </span>
            )}
          </span>
          <Icon svg={ChevronDown} size="x2small" />
        </PopoverTrigger>
        <PopoverContent
          aria-label={t("footer.rewards")}
          align="end"
          sideOffset={16}
          className="w-96 max-w-[calc(100vw_-_2rem)] overflow-hidden rounded-2xl border-outline-grey bg-white p-0 text-primary shadow-lg"
        >
          <div
            style={{ containerType: "inline-size", containerName: "app" }}
            className="bg-one [&_.RewardProgressV2]:shadow-none"
          >
            <RewardProgressV2
              rewardsCount={available.length}
              nextMilestone={next ? next.minimumVisits : 0}
              nextMilestoneRewardsCount={next ? next.rewardsCount : 0}
              cashToUnlock={next?.cashToUnlock}
              unlockProgressPercent={next ? next.unlockProgressPercent : 100}
              marketplaceEnabled={platform.platformSettings?.marketplaceEnabled}
              visitCashValue={platform.platformSettings?.visitCashValue}
              spendBasedRewardsEnabled={platform.platformSettings?.spendBasedRewardsEnabled ?? true}
            />
            <div className="flex justify-center border-t border-outline-grey px-5 py-2">
              <Link to="/rewards" className="py-2 text-bold2 text-brand-color">
                {t("visitHistory.viewRewards")}
              </Link>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {accountLinks}
    </div>
  );
};
